import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Quiz,
  QUIZ_DATA_INITIAL_VALUE,
  Response,
  USER_RESPONSES_INITIAL_VALUE,
  UserResponses,
} from '../common';
import update from 'immutability-helper';
import Progress from '@/components/dataDisplay/Progress';
import StepperButtons from './StepperButtons';
import Question from './Question';

interface Props {
  quizData?: string | null;
  responses?: string | null;
  onSaveUserResponses?: (userResponses: string) => void;
  onFinishQuiz?: () => void;
  onExitQuiz?: () => void;
}
const StepperQuiz: React.FC<Props> = ({
  quizData,
  responses,
  onSaveUserResponses,
  onFinishQuiz,
  onExitQuiz,
}) => {
  const [quiz, setQuiz] = useState<Quiz | null>(QUIZ_DATA_INITIAL_VALUE);
  const [userResponses, setUserResponses] = useState<UserResponses | null>(USER_RESPONSES_INITIAL_VALUE);
  const [step, setStep] = useState(0);

  const getQuestionResponse = useCallback(
    (questionId: string) => {
      return userResponses?.responses.find((response) => response.questionId === questionId)?.answer;
    },
    [userResponses?.responses],
  );

  const currentQuestion = useMemo(() => quiz?.questions[step], [quiz, step]);

  const addUserResponses = useCallback((questionId: string, answer: Response['answer']) => {
    setUserResponses((prevUserResponses) =>
      update(prevUserResponses, {
        responses: {
          $push: [
            {
              questionId,
              answer,
            },
          ],
        },
      }),
    );
  }, []);

  const handleChangeResponse = useCallback(
    (questionId: string) => {
      const responseIndex = userResponses?.responses.findIndex(
        (response) => response.questionId === questionId,
      );
      return (answer: Response['answer']) => {
        if (responseIndex === -1 || responseIndex === undefined) {
          addUserResponses(questionId, answer);
        } else {
          setUserResponses((prevUserResponses) =>
            update(prevUserResponses, {
              responses: {
                [responseIndex]: {
                  answer: { $set: answer },
                },
              },
            }),
          );
        }
      };
    },
    [addUserResponses, userResponses?.responses],
  );

  const handleSaveUserResponses = useCallback(() => {
    if (!userResponses || !onSaveUserResponses) return;
    onSaveUserResponses(JSON.stringify(userResponses));
  }, [userResponses, onSaveUserResponses]);

  // hidrate quiz and response data
  useEffect(() => {
    if (!quizData) return;
    const quiz = JSON.parse(quizData);
    setQuiz(quiz);
  }, [quizData]);

  useEffect(() => {
    if (!responses) return;
    const userResponses = JSON.parse(responses);
    setUserResponses(userResponses);
  }, [responses]);

  if (!quiz || !currentQuestion) return null;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 32, flex: 1, overflow: 'auto', marginTop: 16 }}
    >
      <Progress value={(step * 100) / quiz?.questions.length} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Question
          key={currentQuestion.id}
          id={currentQuestion.id}
          type={currentQuestion.type}
          question={currentQuestion.question}
          options={currentQuestion.options}
          response={getQuestionResponse(currentQuestion.id)}
          onChangeResponse={handleChangeResponse(currentQuestion.id)}
          config={currentQuestion.config}
        />
      </div>
      <StepperButtons
        questionsLength={quiz?.questions.length}
        step={step}
        questionType={currentQuestion.type}
        setStep={setStep}
        onFinishQuiz={onFinishQuiz}
        onExitQuiz={onExitQuiz}
        response={getQuestionResponse(currentQuestion.id)}
        handleSaveUserResponses={handleSaveUserResponses}
      />
    </div>
  );
};

export default StepperQuiz;
